/**
 * Brazilian Portuguese (pt-BR) validation error messages
 * Translates Zod error messages to user-friendly Portuguese
 */

import { type ZodError } from 'zod'
import type { $ZodIssue, $ZodErrorMap, $ZodRawIssue } from 'zod/v4/core'

/**
 * Common error messages in pt-BR
 */
export const ERROR_MESSAGES = {
  // Required field
  required: 'Campo obrigatório',

  // Type errors
  invalid_type: 'Tipo de dado inválido',
  invalid_string: 'Texto inválido',
  invalid_number: 'Número inválido',
  invalid_date: 'Data inválida',
  invalid_boolean: 'Valor booleano inválido',

  // String validations
  too_small_string: 'Mínimo de {min} caracteres',
  too_big_string: 'Máximo de {max} caracteres',
  invalid_email: 'E-mail inválido',
  invalid_url: 'URL inválida',

  // Number validations
  too_small_number: 'Deve ser maior que {min}',
  too_big_number: 'Deve ser menor que {max}',
  not_positive: 'Deve ser um número positivo',
  not_negative: 'Deve ser um número negativo',
  not_nonnegative: 'Deve ser um número não-negativo',

  // Date validations
  too_small_date: 'Data deve ser posterior a {min}',
  too_big_date: 'Data deve ser anterior a {max}',
  future_date: 'Data não pode ser no futuro',
  past_date: 'Data não pode ser no passado',

  // Enum validations
  invalid_enum: 'Valor inválido',
  invalid_enum_values: 'Valor inválido. Opções: {values}',

  // Custom validations
  invalid_cpf: 'CPF inválido. Verifique o formato: 000.000.000-00',
  invalid_phone: 'Telefone inválido. Use formato brasileiro (11) 98765-4321',
  invalid_period: 'Período inválido. Use formato AAAA-MM (ex: 2024-01)',
  invalid_uuid: 'Identificador inválido',

  // General
  custom: 'Valor inválido'
} as const

/**
 * Format error message with dynamic values
 */
function formatMessage(template: string, values: Record<string, any>): string {
  let message = template
  for (const [key, value] of Object.entries(values)) {
    message = message.replace(`{${key}}`, String(value))
  }
  return message
}

/**
 * Get error message for a specific Zod issue type
 */
function getErrorMessage(issue: $ZodIssue): string {
  switch (issue.code) {
    case 'invalid_type':
      // Check if input is undefined or null
      if (issue.input === undefined || issue.input === null) {
        return ERROR_MESSAGES.required
      }
      return ERROR_MESSAGES.invalid_type

    case 'invalid_format':
      // Zod v4 uses 'invalid_format' instead of 'invalid_string'
      if (issue.format === 'email') {
        return ERROR_MESSAGES.invalid_email
      }
      if (issue.format === 'url') {
        return ERROR_MESSAGES.invalid_url
      }
      if (issue.format === 'uuid') {
        return ERROR_MESSAGES.invalid_uuid
      }
      if (issue.format === 'regex') {
        // Check the message for specific regex validation types
        const message = issue.message?.toLowerCase() || ''
        if (message.includes('cpf')) {
          return ERROR_MESSAGES.invalid_cpf
        }
        if (message.includes('telefone') || message.includes('phone')) {
          return ERROR_MESSAGES.invalid_phone
        }
        if (message.includes('período') || message.includes('period')) {
          return ERROR_MESSAGES.invalid_period
        }
      }
      return ERROR_MESSAGES.invalid_string

    case 'too_small':
      // Zod v4 uses 'origin' instead of 'type'
      if (issue.origin === 'string') {
        return formatMessage(ERROR_MESSAGES.too_small_string, {
          min: issue.minimum
        })
      }
      if (issue.origin === 'number' || issue.origin === 'int') {
        return formatMessage(ERROR_MESSAGES.too_small_number, {
          min: issue.minimum
        })
      }
      if (issue.origin === 'date') {
        return formatMessage(ERROR_MESSAGES.too_small_date, {
          min: new Date(issue.minimum as number).toLocaleDateString('pt-BR')
        })
      }
      return ERROR_MESSAGES.custom

    case 'too_big':
      // Zod v4 uses 'origin' instead of 'type'
      if (issue.origin === 'string') {
        return formatMessage(ERROR_MESSAGES.too_big_string, {
          max: issue.maximum
        })
      }
      if (issue.origin === 'number' || issue.origin === 'int') {
        return formatMessage(ERROR_MESSAGES.too_big_number, {
          max: issue.maximum
        })
      }
      if (issue.origin === 'date') {
        return formatMessage(ERROR_MESSAGES.too_big_date, {
          max: new Date(issue.maximum as number).toLocaleDateString('pt-BR')
        })
      }
      return ERROR_MESSAGES.custom

    case 'invalid_value':
      // Zod v4 uses 'invalid_value' instead of 'invalid_enum_value'
      // and 'values' instead of 'options'
      if (issue.values && Array.isArray(issue.values)) {
        return formatMessage(ERROR_MESSAGES.invalid_enum_values, {
          values: issue.values.join(', ')
        })
      }
      return ERROR_MESSAGES.invalid_enum

    case 'custom':
      // Custom validations should provide their own Portuguese messages
      return issue.message || ERROR_MESSAGES.custom

    default:
      // Fallback to the issue message if available
      return issue.message || ERROR_MESSAGES.custom
  }
}

/**
 * Format ZodError to user-friendly field error mapping
 *
 * @param error - ZodError from schema validation
 * @param locale - Locale (defaults to pt-BR)
 * @returns Object mapping field names to error messages
 *
 * @example
 * const result = schema.safeParse(data)
 * if (!result.success) {
 *   const errors = formatZodError(result.error)
 *   // { name: "Mínimo de 2 caracteres", email: "E-mail inválido" }
 * }
 */
export function formatZodError(
  error: ZodError,
  locale: string = 'pt-BR'
): Record<string, string> {
  const fieldErrors: Record<string, string> = {}

  for (const issue of error.issues) {
    // Get the field path (e.g., "personalInfo.name" or "salary")
    const path = issue.path.join('.')

    // Get localized error message
    const message = getErrorMessage(issue)

    // Store only the first error for each field
    if (!fieldErrors[path]) {
      fieldErrors[path] = message
    }
  }

  return fieldErrors
}

/**
 * Format single field error for inline validation
 *
 * @param error - ZodError from field validation
 * @param fieldName - Field name to extract error for
 * @returns Error message for the field or undefined
 *
 * @example
 * const result = schema.shape.email.safeParse(emailValue)
 * if (!result.success) {
 *   const errorMsg = formatFieldError(result.error, 'email')
 *   // "E-mail inválido"
 * }
 */
export function formatFieldError(
  error: ZodError,
  fieldName?: string
): string | undefined {
  const fieldErrors = formatZodError(error)

  if (fieldName) {
    return fieldErrors[fieldName]
  }

  // Return the first error if no field name specified
  return Object.values(fieldErrors)[0]
}

/**
 * Get all error messages as an array
 *
 * @param error - ZodError from schema validation
 * @returns Array of error messages
 *
 * @example
 * const result = schema.safeParse(data)
 * if (!result.success) {
 *   const messages = getAllErrorMessages(result.error)
 *   // ["Nome deve ter no mínimo 2 caracteres", "E-mail inválido"]
 * }
 */
export function getAllErrorMessages(error: ZodError): string[] {
  const fieldErrors = formatZodError(error)
  return Object.values(fieldErrors)
}

/**
 * Check if a specific field has errors
 *
 * @param error - ZodError from schema validation
 * @param fieldName - Field name to check
 * @returns True if field has errors
 */
export function hasFieldError(error: ZodError, fieldName: string): boolean {
  return error.issues.some((issue) => issue.path.join('.') === fieldName)
}

/**
 * Custom error map for Zod to use pt-BR messages by default
 * Can be used with z.setErrorMap(customErrorMap)
 *
 * @example
 * import { z } from 'zod'
 * import { customErrorMap } from './locales/pt-BR/validation'
 *
 * // Set globally
 * z.setErrorMap(customErrorMap)
 */
export const customErrorMap: $ZodErrorMap = (issue) => {
  // Use custom message if provided in the issue
  if (issue.message) {
    return { message: issue.message }
  }

  // Build a complete $ZodIssue from the raw issue for getErrorMessage
  const completeIssue: $ZodIssue = {
    ...issue,
    message: issue.message || '',
    path: issue.path || []
  } as $ZodIssue

  // Use localized message based on issue type
  return { message: getErrorMessage(completeIssue) }
}
